import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Save, Send } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { SERVICE_TYPES, UNITS, money } from "@/lib/format";

const emptyItem = () => ({
    description: "",
    quantity: 1,
    unit: "sq_ft",
    unit_price: 0,
    kind: "labor",
});

export default function EstimateForm() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const fromLeadId = params.get("lead");

    const [client, setClient] = useState({ name: "", email: "", phone: "", address: "" });
    const [jobAddress, setJobAddress] = useState("");
    const [serviceType, setServiceType] = useState("interior");
    const [items, setItems] = useState([emptyItem()]);
    const [notes, setNotes] = useState("");
    const [expirationDays, setExpirationDays] = useState(30);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    // hydrate from lead if provided
    useEffect(() => {
        if (!fromLeadId) return;
        api.get(`/admin/leads/${fromLeadId}`)
            .then(({ data }) => {
                setClient({
                    name: data.name || "",
                    email: data.email || "",
                    phone: data.phone || "",
                    address: data.property_address || "",
                });
                setJobAddress(data.property_address || "");
                if (data.description) setNotes(data.description);
                const st = (data.service_type || "").toLowerCase();
                if (st.includes("exterior")) setServiceType("exterior");
                else if (st.includes("commercial")) setServiceType("commercial");
                else if (st.includes("specialty")) setServiceType("specialty");
                else setServiceType("interior");
            })
            .catch(() => {});
    }, [fromLeadId]);

    const totals = useMemo(() => {
        let labor = 0;
        let materials = 0;
        items.forEach((i) => {
            const t = Number(i.quantity || 0) * Number(i.unit_price || 0);
            if (i.kind === "materials") materials += t;
            else labor += t;
        });
        return {
            labor: Math.round(labor * 100) / 100,
            materials: Math.round(materials * 100) / 100,
            grand: Math.round((labor + materials) * 100) / 100,
        };
    }, [items]);

    const updateItem = (idx, patch) => {
        setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    };

    const submit = async (send) => {
        setErr("");
        setSaving(true);
        try {
            const payload = {
                client: {
                    name: client.name.trim(),
                    email: client.email.trim() || null,
                    phone: client.phone.trim(),
                    address: client.address.trim(),
                },
                job_address: jobAddress.trim(),
                service_type: serviceType,
                line_items: items.map((i) => ({
                    description: i.description.trim(),
                    quantity: Number(i.quantity),
                    unit: i.unit,
                    unit_price: Number(i.unit_price),
                    kind: i.kind,
                })),
                notes,
                expiration_days: Number(expirationDays),
                ...(fromLeadId ? { lead_id: fromLeadId } : {}),
            };
            const { data } = await api.post("/estimates", payload);
            const id = data.estimate.id;
            if (send) {
                try {
                    await api.post(`/estimates/${id}/send`);
                } catch (e) {
                    // still navigate to detail
                }
            }
            navigate(`/estimates/${id}`, { replace: true });
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="page-shell">
            <Link
                to="/estimates"
                className="link-red inline-flex items-center gap-1 text-sm"
                data-testid="ef-back"
            >
                <ArrowLeft size={14} />
                Back to estimates
            </Link>

            <header className="fade-up mt-4">
                <p className="eyebrow">New estimate</p>
                <h1 className="h1 mt-3" data-testid="ef-title">
                    Build the estimate
                </h1>
                <p className="text-muted mt-2 max-w-[640px]">
                    Add line items, set the expiration, and either save as a draft or send it
                    straight to the client.
                </p>
            </header>

            {err && (
                <div
                    className="brand-card mt-5"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    data-testid="ef-error"
                >
                    {err}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
                {/* Client + job */}
                <div className="brand-card lg:col-span-2 fade-up">
                    <p className="eyebrow">Client</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <div>
                            <label className="field-label">Name</label>
                            <input
                                className="field-input"
                                value={client.name}
                                onChange={(e) => setClient({ ...client, name: e.target.value })}
                                required
                                data-testid="ef-client-name"
                            />
                        </div>
                        <div>
                            <label className="field-label">Email</label>
                            <input
                                type="email"
                                className="field-input"
                                value={client.email}
                                onChange={(e) => setClient({ ...client, email: e.target.value })}
                                data-testid="ef-client-email"
                            />
                        </div>
                        <div>
                            <label className="field-label">Phone</label>
                            <input
                                className="field-input"
                                value={client.phone}
                                onChange={(e) => setClient({ ...client, phone: e.target.value })}
                                data-testid="ef-client-phone"
                            />
                        </div>
                        <div>
                            <label className="field-label">Service type</label>
                            <select
                                className="field-select"
                                value={serviceType}
                                onChange={(e) => setServiceType(e.target.value)}
                                data-testid="ef-service-type"
                            >
                                {SERVICE_TYPES.map((s) => (
                                    <option key={s} value={s} className="capitalize">
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="sm:col-span-2">
                            <label className="field-label">Job address</label>
                            <input
                                className="field-input"
                                value={jobAddress}
                                onChange={(e) => setJobAddress(e.target.value)}
                                required
                                data-testid="ef-job-address"
                            />
                        </div>
                    </div>
                </div>

                {/* Totals sidebar */}
                <div className="brand-card fade-up delay-1" data-testid="ef-totals">
                    <p className="eyebrow">Live total</p>
                    <div className="mt-4 space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-muted">Labor</span>
                            <span className="font-mono">{money(totals.labor)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted">Materials</span>
                            <span className="font-mono">{money(totals.materials)}</span>
                        </div>
                        <div
                            className="flex justify-between pt-3 border-t border-[var(--brand-surface)]"
                            style={{ fontSize: "1.5rem" }}
                        >
                            <span className="font-display font-bold">Total</span>
                            <span
                                className="font-display font-bold font-mono"
                                style={{ color: "var(--brand-red)" }}
                                data-testid="ef-grand-total"
                            >
                                {money(totals.grand)}
                            </span>
                        </div>
                    </div>
                    <label className="field-label mt-5">Expires in</label>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min="1"
                            max="365"
                            className="field-input"
                            value={expirationDays}
                            onChange={(e) => setExpirationDays(e.target.value)}
                            data-testid="ef-expiration-days"
                        />
                        <span className="text-sm text-muted">days</span>
                    </div>
                </div>
            </div>

            {/* Line items */}
            <div className="brand-card mt-5 fade-up delay-2" data-testid="ef-items-card">
                <div className="flex items-center justify-between">
                    <p className="eyebrow">Line items</p>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setItems([...items, emptyItem()])}
                        data-testid="ef-add-item"
                    >
                        <Plus size={14} />
                        Add line
                    </button>
                </div>

                <div className="mt-4 flex flex-col gap-3">
                    {items.map((it, idx) => (
                        <div
                            key={idx}
                            className="grid grid-cols-12 gap-2 items-end"
                            data-testid={`ef-item-${idx}`}
                        >
                            <div className="col-span-12 md:col-span-5">
                                <label className="field-label">Description</label>
                                <input
                                    className="field-input"
                                    value={it.description}
                                    onChange={(e) =>
                                        updateItem(idx, { description: e.target.value })
                                    }
                                    placeholder="e.g. Wall prep + paint, 3 bedrooms"
                                    data-testid={`ef-item-desc-${idx}`}
                                />
                            </div>
                            <div className="col-span-4 md:col-span-1">
                                <label className="field-label">Qty</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="field-input"
                                    value={it.quantity}
                                    onChange={(e) =>
                                        updateItem(idx, { quantity: e.target.value })
                                    }
                                    data-testid={`ef-item-qty-${idx}`}
                                />
                            </div>
                            <div className="col-span-4 md:col-span-2">
                                <label className="field-label">Unit</label>
                                <select
                                    className="field-select"
                                    value={it.unit}
                                    onChange={(e) =>
                                        updateItem(idx, { unit: e.target.value })
                                    }
                                    data-testid={`ef-item-unit-${idx}`}
                                >
                                    {UNITS.map((u) => (
                                        <option key={u} value={u}>
                                            {u.replace("_", " ")}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-span-4 md:col-span-2">
                                <label className="field-label">Price</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="field-input"
                                    value={it.unit_price}
                                    onChange={(e) =>
                                        updateItem(idx, { unit_price: e.target.value })
                                    }
                                    data-testid={`ef-item-price-${idx}`}
                                />
                            </div>
                            <div className="col-span-8 md:col-span-1">
                                <label className="field-label">Kind</label>
                                <select
                                    className="field-select"
                                    value={it.kind}
                                    onChange={(e) =>
                                        updateItem(idx, { kind: e.target.value })
                                    }
                                    data-testid={`ef-item-kind-${idx}`}
                                >
                                    <option value="labor">Labor</option>
                                    <option value="materials">Materials</option>
                                </select>
                            </div>
                            <div className="col-span-4 md:col-span-1 flex justify-end pb-1">
                                <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={() =>
                                        setItems((prev) => prev.filter((_, i) => i !== idx))
                                    }
                                    disabled={items.length === 1}
                                    aria-label="Remove line"
                                    data-testid={`ef-item-remove-${idx}`}
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Notes */}
            <div className="brand-card mt-5 fade-up delay-3">
                <label className="field-label">Notes (visible to client)</label>
                <textarea
                    rows={4}
                    className="field-textarea"
                    placeholder="Scope detail, timing assumptions, exclusions…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="ef-notes"
                />
            </div>

            {/* Footer actions */}
            <div className="mt-6 flex flex-wrap gap-3 fade-up delay-4">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => submit(false)}
                    disabled={saving}
                    data-testid="ef-save-draft"
                >
                    <Save size={16} />
                    {saving ? "Saving…" : "Save as draft"}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => submit(true)}
                    disabled={saving || !client.email}
                    title={!client.email ? "Add a client email to enable sending" : "Save and email to client"}
                    data-testid="ef-save-send"
                >
                    <Send size={16} />
                    Save &amp; send
                </button>
                {!client.email && (
                    <span className="text-sm text-muted self-center">
                        Add a client email to enable sending.
                    </span>
                )}
            </div>
        </div>
    );
}
